import React, { useState } from "react";
import {
  CompetenceBaseWithChildrenEx,
  getSecondLevelCompetences,
} from "@/server/backend-api";
import { Head } from "@/components/Head";
import { GetServerSidePropsResult } from "next/types";
import { withErrorReportingAndAuthorization } from "../../middlewares/withErrorReportingAndAuthorisation";
import { MotivationScanFirstStep } from "@/components/MotivationScan/MotivationScanFirstStep";
import { MotivationScanSecondStep } from "@/components/MotivationScan/MotivationScanSecondStep";
import {
  MotivationScanAnswers,
  MotivationScanLike,
} from "@/utils/motivationScan";
import { useRouter } from "next/router";
import { Section, SectionContainer } from "@/components/Sections";
import { NextPageWithLayout } from "../../types";
import { UpdateMotivationScanAnswersReq } from "@/types/apiTypes";
import { sortTopicsInMotivationScan } from "@/utils/competenceTree";

type PageProps = {
  competences: CompetenceBaseWithChildrenEx[];
};

const getServerSidePropsImpl = async (): Promise<
  GetServerSidePropsResult<PageProps>
> => {
  const secondLevelCompetences = await getSecondLevelCompetences();
  const competences = await sortTopicsInMotivationScan(secondLevelCompetences);
  return {
    props: { competences },
  };
};

export const getServerSideProps = withErrorReportingAndAuthorization(
  getServerSidePropsImpl
);

const MotivationScanPage: NextPageWithLayout<PageProps> = ({ competences }) => {
  const router = useRouter();
  const [pageNumber, setPageNumber] = useState(1);
  const [answers, setAnswers] = useState<MotivationScanAnswers>(
    competences.reduce((acc, c) => {
      acc[c.id] = {
        competenceId: c.id,
      };
      return acc;
    }, {} as MotivationScanAnswers)
  );

  return (
    <>
      <Head title={"Motivation scan"} description={""} />
      <Section isFirstSection color="light-blue" sectionPadding={true}>
        <SectionContainer defaultPadding={false}>
          <div className="flex flex-col justify-center items-center -mt-xl tablet-lg:mt-0">
            <span className="text-style-caption tablet:text-style-body1">
              {pageNumber}/2
            </span>
          </div>
        </SectionContainer>

        {pageNumber === 1 && (
          <MotivationScanFirstStep
            competences={competences}
            answers={answers}
            onNextClicked={() => {
              setPageNumber(2);
            }}
            onCardDislikeClicked={(c) => {
              setAnswers((prev) => {
                const next = { ...prev };
                next[c.id] = {
                  competenceId: c.id,
                  likeOrDislike: MotivationScanLike.Dislike,
                };
                return next;
              });
            }}
            onCardLikeClicked={(c) => {
              setAnswers((prev) => {
                const next = { ...prev };
                next[c.id] = {
                  competenceId: c.id,
                  likeOrDislike: MotivationScanLike.Like,
                };
                return next;
              });
            }}
          />
        )}
        {pageNumber === 2 && (
          <MotivationScanSecondStep
            competences={competences}
            answers={answers}
            onGetResultClicked={async () => {
              const body: UpdateMotivationScanAnswersReq = {
                answers: answers,
              };
              await fetch("/api/motivation-scan/update-result", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
              });
              await router.push("/motivation-scan/my-results");
            }}
            onPreviousClicked={() => {
              setPageNumber(1);
            }}
            onSubcompetenceClicked={(c, likedOrDisliked) => {
              setAnswers((prev) => {
                const next = { ...prev };
                next[c.id] = {
                  competenceId: c.id,
                  likeOrDislike: likedOrDisliked,
                };
                return next;
              });
            }}
            onSubcompetenceSelectDeselectAllClicked={(c, selectOrDeselect) => {
              setAnswers((prev) => {
                const next = { ...prev };
                setCompetenceAndChildrenLike(
                  c,
                  next,
                  selectOrDeselect === "select"
                    ? MotivationScanLike.Like
                    : MotivationScanLike.Dislike
                );
                return next;
              });
            }}
          />
        )}
      </Section>
    </>
  );
};

function setCompetenceAndChildrenLike(
  c: CompetenceBaseWithChildrenEx,
  answers: MotivationScanAnswers,
  likeOrDislike: MotivationScanLike
) {
  // Skip setting top level competence to like or dislike.
  c.children.forEach((child) => {
    setCompetenceAndChildrenLikeImpl(child, answers, likeOrDislike);
  });
}

function setCompetenceAndChildrenLikeImpl(
  c: CompetenceBaseWithChildrenEx,
  answers: MotivationScanAnswers,
  likeOrDislike: MotivationScanLike
) {
  answers[c.id] = {
    competenceId: c.id,
    likeOrDislike: likeOrDislike,
  };
  c.children.forEach((child) => {
    setCompetenceAndChildrenLikeImpl(child, answers, likeOrDislike);
  });
}

MotivationScanPage.layout = {
  hideFooter: true,
};

export default MotivationScanPage;
